import React, { useEffect, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/Modal";
import { buildInitialChannels, normalizePrivacyVisibility } from "../../../features/privacy/definitions";
import PrivacyChannelList from "../family/components/PrivacyChannelList";
import PrivacyVisibilityCard from "../family/components/PrivacyVisibilityCard";

type EntityId = string | number;
type PrivacyVisibility = "VISIBLE" | "NOT_VISIBLE" | "UNSET";
type Consent = Record<string, unknown> & { visibility?: PrivacyVisibility | null };
export type PrivacyReviewItem = {
  childId: EntityId;
  childName: string;
  status?: string | null;
};

// The backend only sets a status when the school enabled the annual privacy review.
export const needsPrivacyReview = (status?: string | null) => {
  const normalized = (status || "").toUpperCase();
  return normalized === "MISSING" || normalized === "EXPIRED";
};

// The popup can be switched off per school while the privacy action itself stays available.
export const shouldShowPrivacyReviewPopup = (
  schoolSettings: Record<string, unknown> | null | undefined,
  items: Array<{ status?: string | null }>
) => schoolSettings?.privacyReviewPopupEnabled !== false && items.some((item) => needsPrivacyReview(item.status));

type Props = {
  items: PrivacyReviewItem[];
  // School-configured content (privacyReviewPopupTitle / privacyReviewPopupMessage).
  title: string;
  message: string;
  privacyByChild: Record<string, { data?: Consent | null; loading?: boolean; error?: unknown }>;
  loadPrivacyFor: (childId: EntityId) => Promise<void>;
  savePrivacyFor: (childId: EntityId, payload: Consent) => Promise<void>;
  onRemindLater: () => void;
};

type Draft = { channels: Record<string, boolean>; visibility: PrivacyVisibility };

const PrivacyReviewModal = ({ items, title, message, privacyByChild, loadPrivacyFor, savePrivacyFor, onRemindLater }: Props) => {
  const { t } = useTranslation();
  // Choices stay local per child until the parent confirms the whole review.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [reviewed, setReviewed] = useState<string[]>([]);
  // null = everything collapsed by the parent.
  const [openKey, setOpenKey] = useState<string | null>(() => (items[0] ? String(items[0].childId) : null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const childIds = items.map((item) => String(item.childId)).join("|");
  useEffect(() => {
    items.forEach((item) => void loadPrivacyFor(item.childId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childIds, loadPrivacyFor]);

  // Prefill each child once from its stored consent; later edits are never overwritten.
  useEffect(() => {
    setDrafts((previous) => {
      let next = previous;
      items.forEach((item) => {
        const key = String(item.childId);
        const state = privacyByChild[key];
        if (previous[key] || !state || state.loading || state.error) return;
        next = {
          ...next,
          [key]: {
            channels: buildInitialChannels(state.data),
            visibility: normalizePrivacyVisibility(state.data?.visibility) as PrivacyVisibility,
          },
        };
      });
      return next;
    });
  }, [items, privacyByChild]);

  if (!items.length) return null;

  const isValid = (key: string) => !!drafts[key] && drafts[key].visibility !== "UNSET";
  // Every child must be explicitly marked in this session before the review can be confirmed.
  const isReviewed = (key: string) => reviewed.includes(key) && isValid(key);
  const keys = items.map((item) => String(item.childId));
  const reviewedCount = keys.filter(isReviewed).length;
  // A child that was saved leaves `items`; fall back to the next one still to review.
  const activeKey =
    openKey === null || keys.includes(openKey) ? openKey : keys.find((key) => !isReviewed(key)) ?? null;

  const updateDraft = (key: string, change: (draft: Draft) => Draft) =>
    setDrafts((previous) => (previous[key] ? { ...previous, [key]: change(previous[key]) } : previous));

  const completeChild = (key: string) => {
    if (!isValid(key)) return;
    setReviewed((previous) => (previous.includes(key) ? previous : [...previous, key]));
    setOpenKey(keys.find((other) => other !== key && !isReviewed(other)) ?? null);
  };

  const confirm = async () => {
    setSaving(true);
    setSaveError(false);
    try {
      for (const item of items) {
        const { channels, visibility } = drafts[String(item.childId)];
        await savePrivacyFor(item.childId, { ...channels, visibility: visibility === "UNSET" ? null : visibility });
      }
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show
      variant="lds"
      type="info"
      title={title}
      onClose={onRemindLater}
      hideDefaultActions
      dialogClassName="sm:max-w-2xl"
    >
      <div data-testid="privacy-review-modal">
        <p className="mb-2 whitespace-pre-line text-sm leading-6 text-slate-600 dark:text-slate-300">
          {message}
        </p>
        <p className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="privacy-review-progress">
          {reviewedCount > 0
            ? t("privacy.review.reviewed_progress", {
                defaultValue: "{{reviewed}} of {{count}} reviewed",
                reviewed: reviewedCount,
                count: items.length,
              })
            : t("privacy.review.progress", { defaultValue: "{{count}} child profile(s) need review", count: items.length })}
        </p>

        <div className="divide-y divide-slate-200 border-y border-slate-200 dark:divide-slate-700 dark:border-slate-700">
          {items.map((item) => {
            const key = String(item.childId);
            const state = privacyByChild[key];
            const draft = drafts[key];
            const open = activeKey === key;
            const done = isReviewed(key);
            const hasNext = keys.some((other) => other !== key && !isReviewed(other));

            return (
              <div key={key}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={`privacy-review-panel-${key}`}
                  onClick={() => setOpenKey(open ? null : key)}
                  className="flex w-full items-center gap-3 py-3 text-left"
                >
                  <strong className="min-w-0 flex-1 truncate text-base text-slate-950 dark:text-white">{item.childName}</strong>
                  <span
                    className={`shrink-0 text-xs font-semibold ${
                      done ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"
                    }`}
                  >
                    {done
                      ? t("privacy.review.status_reviewed", { defaultValue: "Reviewed" })
                      : t("privacy.review.status_pending", { defaultValue: "To review" })}
                  </span>
                  <FiChevronDown
                    aria-hidden="true"
                    className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
                  />
                </button>

                {open && (
                  <div id={`privacy-review-panel-${key}`} className="pb-4">
                    {state?.error ? (
                      <p role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-200">
                        {t("privacy.load_error", { defaultValue: "Could not load privacy settings." })}
                      </p>
                    ) : !draft ? (
                      <p className="py-8 text-center text-sm text-slate-500">
                        {t("privacy.review.loading", { defaultValue: "Loading privacy settings…" })}
                      </p>
                    ) : (
                      <>
                        <PrivacyChannelList
                          t={t}
                          channels={draft.channels}
                          setChannels={(change: (channels: Record<string, boolean>) => Record<string, boolean>) =>
                            updateDraft(key, (current) => ({ ...current, channels: change(current.channels) }))
                          }
                          footer={null}
                          visibilitySlot={
                            <PrivacyVisibilityCard
                              t={t}
                              childKey={`review-${key}`}
                              visibility={draft.visibility}
                              setVisibility={(visibility: PrivacyVisibility) =>
                                updateDraft(key, (current) => ({ ...current, visibility }))
                              }
                            />
                          }
                        />
                        {draft.visibility === "UNSET" && (
                          <p role="status" className="mt-3 text-sm font-semibold text-amber-700 dark:text-amber-300">
                            {t("privacy.review.visibility_required", {
                              defaultValue: "Choose whether your child may be recognizable before confirming.",
                            })}
                          </p>
                        )}
                        {!done && (
                          <div className="mt-3 flex justify-end">
                            <button
                              type="button"
                              onClick={() => completeChild(key)}
                              disabled={!isValid(key)}
                              className="rounded-xl px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                            >
                              {hasNext
                                ? t("privacy.review.next_child", { defaultValue: "Next child" })
                                : t("privacy.review.mark_reviewed", { defaultValue: "Mark as reviewed" })}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {saveError && (
          <p role="alert" className="mt-3 text-sm font-semibold text-rose-600 dark:text-rose-300">
            {t("privacy.review.save_error", { defaultValue: "Could not confirm the privacy settings. Please try again." })}
          </p>
        )}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onRemindLater} disabled={saving} className="rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800">
            {t("privacy.review.remind_later", { defaultValue: "Remind me later" })}
          </button>
          <button type="button" onClick={confirm} disabled={saving || !keys.every(isReviewed)} className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
            {saving
              ? t("privacy.review.confirming", { defaultValue: "Confirming…" })
              : t("privacy.review.confirm", { defaultValue: "Confirm" })}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyReviewModal;
